import { Icons } from '@/assets/icons/icons';
import { YStack } from '@/components/ui/layout/layout';

const LIST_FRAME =
  'divide-y divide-border overflow-hidden rounded-lg border border-border bg-surface';

/** `waiting`: Littlebird retries on its own once the user acts */
export type CheckState = 'pending' | 'checking' | 'ok' | 'waiting' | 'blocked';

export interface Check {
  title: string;
  state: CheckState;
  detail: string;
  action?: React.ReactNode;
}

function CheckIcon({ state }: { state: CheckState }) {
  switch (state) {
    case 'pending':
      return <Icons.circle className="size-4 text-foreground-subtle" />;
    case 'checking':
      return (
        <Icons.loader className="size-4 animate-spin text-foreground-muted" />
      );
    case 'ok':
      return (
        <span className="flex size-4 items-center justify-center rounded-full bg-success-3 text-success-11">
          <Icons.check className="size-3" />
        </span>
      );
    case 'waiting':
      return <Icons.loader className="size-4 animate-spin text-warning-11" />;
    case 'blocked':
      return <Icons.warn className="size-4 text-warning-11" />;
  }
}

/** A labelled, framed list, as the connect dialogs and settings tabs show it */
export function ListSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <YStack className="gap-2">
      <span className="text-xs font-medium text-foreground-muted">{label}</span>
      <ul className={LIST_FRAME}>{children}</ul>
    </YStack>
  );
}

export function CapabilityRow({
  title,
  note,
}: {
  title: string;
  note?: string;
}) {
  return (
    <li className="flex items-center gap-3 px-4 py-2.5">
      <span className="min-w-0 flex-1 text-sm text-foreground">{title}</span>
      {note && (
        <span className="shrink-0 text-xs text-foreground-muted">{note}</span>
      )}
    </li>
  );
}

export function CheckRow({ check }: { check: Check }) {
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <CheckIcon state={check.state} />
      <YStack className="min-w-0 flex-1 gap-0.5">
        <span className="text-sm text-foreground">{check.title}</span>
        <span className="text-xs text-foreground-muted">{check.detail}</span>
      </YStack>
      {check.action}
    </li>
  );
}
